import { FC, useState } from 'react';
import { Tab } from '@mui/material';
import { googleTagManager, removeLoanNumberFromURL } from 'use/gtm';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import RealEstateDetails from './components/RealEstate/RealEstateDetails';
import LoanDetails from './components/Loan/LoanDetails';
import ConstructionDepotDetails from './components/Depots/DepotDetails';
import ContactDetails from './components/Contact/ContactDetails';
import PersonalDetails from './components/Person/PersonalDetails';
import CollectionDetails from './components/CollectionAgreements/CollectionDetails';
import {
  PdfCircularProgress,
  PdfIconButton,
  PDFIconDiv,
  Tabs,
} from 'screens/Clients/Clients.styled';
import { ClientDetailsModel } from 'types/ClientDetails/ClientDetailsModel';
import pdfIcon from 'assets/images/icons/pdf-gray.svg';
import { getLoandataPdf } from 'api/downloads';
import fileDownload from 'js-file-download';
import CustomSnackbar from 'components/Snackbar/CustomSnackbar';
import DeedsDetails from './components/Deeds/DeedsDetails';
import TariffClassDetails from './components/TariffClass/TariffClassDetail';

export type ClientDataContentProps = {
  commercialLabel: string;
  loanNumber: number;
  clientData: ClientDetailsModel;
};

const ClientDataContent: FC<ClientDataContentProps> = ({
  commercialLabel,
  loanNumber,
  clientData,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState(0);
  const [isError, setError] = useState<boolean>(false);
  const [isPdfLoading, setPdfLoading] = useState(false);

  const {
    realEstates,
    loan,
    loanParts,
    constructionDepot,
    sustainabilityDepot,
    persons,
    bridgings,
    collectionAgreement,
    deeds,
    tariffClass,
  } = clientData;

  const translationRoot = 'clients.clientData.tabs';

  const getAvailableTabs = (): string[] => {
    const availableTabs = [];
    if (loan) availableTabs.push('loan');
    if (realEstates && !!realEstates[0]) availableTabs.push('realEstate');
    if (
      (!!constructionDepot && Object.keys(constructionDepot).length) ||
      (!!sustainabilityDepot && Object.keys(sustainabilityDepot).length)
    )
      availableTabs.push('depots');
    if (persons) {
      availableTabs.push('contact');
      availableTabs.push('personal');
    }
    if (collectionAgreement) {
      availableTabs.push('collectionAgreement');
    }
    if (deeds?.length) {
      availableTabs.push('deeds');
    }
    if (tariffClass) {
      availableTabs.push('tariffClass');
    }

    return availableTabs;
  };

  const tabs = getAvailableTabs();

  const onChangeTab = (e: React.ChangeEvent<any>, newValue: number) => {
    const tab = tabs[newValue];
    googleTagManager({
      event: 'TabClick',
      btnName: `Navigation-Tab-${tab}`,
    });
    googleTagManager({
      event: 'Navigate',
      btnName: `Tab-${tab}`,
    });
    setCurrentTab(newValue);
  };

  const sumOfLoanPartsInstallment: number = loanParts.reduce<number>((sum, part) => {
    const total = part.installment?.totalAmount ?? 0;
    return sum + total;
  }, 0);

  const isCurrentTab = (tab: string) => {
    switch (tab) {
      case 'loan':
        return <LoanDetails loan={loan} loanParts={loanParts} bridgings={bridgings} />;
      case 'realEstate':
        return realEstates && <RealEstateDetails realEstate={realEstates[0]} />;
      case 'deeds':
        return deeds && <DeedsDetails deeds={deeds} />;
      case 'depots':
        return (
          <ConstructionDepotDetails
            constructionDepot={constructionDepot}
            sustainabilityDepot={sustainabilityDepot}
          />
        );
      case 'contact':
        return persons && <ContactDetails persons={persons} />;
      case 'personal':
        return persons && <PersonalDetails persons={persons} />;
      case 'collectionAgreement':
        return (
          collectionAgreement && (
            <CollectionDetails
              sumOfLoanPartsInstallment={sumOfLoanPartsInstallment}
              collectionAgreement={collectionAgreement}
            />
          )
        );
      case 'tariffClass':
        return <TariffClassDetails tariffClass={tariffClass} />;
      default:
        return null;
    }
  };

  const getFileName = (contentDisposition: string) => {
    contentDisposition = contentDisposition.substring(
      contentDisposition.indexOf('filename=') + 9,
      contentDisposition.length
    );
    contentDisposition = contentDisposition.substring(0, contentDisposition.indexOf(';'));
    return contentDisposition;
  };

  const handleDownload = async () => {
    try {
      setPdfLoading(true);

      const response = await getLoandataPdf(commercialLabel, loanNumber);
      const octetStreamMime = 'application/octet-stream';
      const contentType = response.headers['content-type'] || octetStreamMime;
      const blob = new Blob([response.data], { type: contentType });
      const filename = getFileName(response.headers['content-disposition']);
      const file = new File([blob!], '', {
        type: blob.type,
      });

      googleTagManager({
        page: { url: removeLoanNumberFromURL(location.pathname) },
        event: 'DownloadClientOverview',
        file: filename,
        fileType: 'application/pdf',
      });

      if (file) {
        fileDownload(file, filename);
      } else {
        setError(true);
      }
    } catch (error) {
      setError(true);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <>
      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        value={currentTab}
        onChange={onChangeTab}
        indicatorColor="primary"
        TabScrollButtonProps={{
          sx: {
            // hide the left‐hand scroll button permanently
            '&[aria-label="scroll left"]': {
              display: 'none',
            },
          },
        }}
      >
        {tabs.map((tabName) => (
          <Tab
            key={tabName}
            label={t(`${translationRoot}.${tabName}`)}
            data-testid={`clientdata-tab-${tabName}`}
          />
        ))}
        <PDFIconDiv>
          {isPdfLoading ? (
            <PdfCircularProgress size={25} />
          ) : (
            <PdfIconButton onClick={handleDownload}>
              <img width="32" height="32" src={pdfIcon} alt="file-icon" />
            </PdfIconButton>
          )}
        </PDFIconDiv>
      </Tabs>
      <CustomSnackbar
        isOpen={isError}
        handleClose={() => setError(false)}
        severity="error"
        message={t('error.default')}
      />
      {isCurrentTab(tabs[currentTab])}
    </>
  );
};

export default ClientDataContent;
